import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import type {
  TGuaranteesApplication,
  TGuaranteesManagerTerms,
  TPatchGuaranteesManagerTermsPayload,
} from '~/store/guarantees/types.ts';
import FlowSection from '../../components/root/shared/FlowSection.tsx';
import {
  BENEFICIARY_SANCTIONS_STATUS_OPTIONS,
  MANAGER_TERMS_TEXT,
} from './constants.ts';
import {
  GuaranteesManagerTermsFieldName,
  type TGuaranteesManagerTermsFormValues,
} from './types.ts';
import {
  buildGuaranteesManagerTermsPayload,
  getGuaranteesManagerTermsFromPatchResponse,
  getGuaranteesManagerTermsInitialValues,
  getGuaranteesManagerTermsResolver,
} from './utils.ts';

type TManagerTermsSubmitResult = TGuaranteesManagerTerms | TGuaranteesApplication | null;

interface IManagerTermsSectionProps {
  isDisabled?: boolean;
  isConfirming?: boolean;
  isLoading: boolean;
  isSaving: boolean;
  managerTerms: TGuaranteesManagerTerms | null;
  requireChangesToEnableActions?: boolean;
  showConfirmButton?: boolean;
  onConfirm: (payload: TPatchGuaranteesManagerTermsPayload) => Promise<TManagerTermsSubmitResult>;
  onSubmit: (payload: TPatchGuaranteesManagerTermsPayload) => Promise<TManagerTermsSubmitResult>;
}

const ManagerTermsSection: React.FC<IManagerTermsSectionProps> = ({
  isDisabled = false,
  isConfirming = false,
  isLoading,
  isSaving,
  managerTerms,
  requireChangesToEnableActions = false,
  showConfirmButton = true,
  onConfirm,
  onSubmit,
}) => {
  const defaultValues = React.useMemo(
    () => getGuaranteesManagerTermsInitialValues(managerTerms),
    [managerTerms]
  );
  const hasBeneficiarySanctionsMatch =
    managerTerms?.beneficiarySanctionsMatchDetected === true;
  const resolver = React.useMemo(
    () => getGuaranteesManagerTermsResolver(hasBeneficiarySanctionsMatch),
    [hasBeneficiarySanctionsMatch]
  );
  const formMethods = useForm<TGuaranteesManagerTermsFormValues>({
    defaultValues,
    resolver,
    mode: 'onSubmit',
  });
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = formMethods;
  const isInteractionBlocked = isSaving || isConfirming;
  const areFieldsDisabled = isInteractionBlocked || isDisabled;
  const isSaveDisabled =
    isInteractionBlocked ||
    isDisabled ||
    (requireChangesToEnableActions && !isDirty);
  const isConfirmDisabled = areFieldsDisabled;
  const actionStartIcon = <ButtonLoadingIcon />;
  const saveStartIcon = isSaving ? actionStartIcon : undefined;
  const confirmStartIcon = isConfirming ? actionStartIcon : undefined;

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const resetWithSubmitResult = (
    result: TManagerTermsSubmitResult,
    fallbackValues: TGuaranteesManagerTermsFormValues
  ) => {
    const responseManagerTerms = getGuaranteesManagerTermsFromPatchResponse(result);

    reset(
      responseManagerTerms
        ? getGuaranteesManagerTermsInitialValues(responseManagerTerms)
        : fallbackValues
    );
  };

  const handleManagerTermsSubmit: SubmitHandler<TGuaranteesManagerTermsFormValues> = async (
    values
  ) => {
    try {
      const result = await onSubmit(
        buildGuaranteesManagerTermsPayload(values, hasBeneficiarySanctionsMatch)
      );

      resetWithSubmitResult(result, values);
    } catch {
      // Request feedback is handled by the page-level callback.
    }
  };

  const handleManagerTermsConfirm: SubmitHandler<TGuaranteesManagerTermsFormValues> = async (
    values
  ) => {
    try {
      const result = await onConfirm(
        buildGuaranteesManagerTermsPayload(values, hasBeneficiarySanctionsMatch)
      );

      resetWithSubmitResult(result, values);
    } catch {
      // Request feedback is handled by the page-level callback.
    }
  };
  const handleConfirmClick = () => {
    void handleSubmit(handleManagerTermsConfirm)();
  };

  return (
    <FlowSection
      step={MANAGER_TERMS_TEXT.sectionStepLabel}
      title={MANAGER_TERMS_TEXT.sectionTitle}
      description={MANAGER_TERMS_TEXT.sectionDescription}
    >
      {isLoading ? (
        <Box sx={guaranteesClientPageStyles.flowLoadingState}>
          <CircularProgress size={24} />
          <Typography sx={guaranteesClientPageStyles.flowLockHint}>
            {MANAGER_TERMS_TEXT.loadingText}
          </Typography>
        </Box>
      ) : (
        <FormProvider {...formMethods}>
          <Box
            component="form"
            onSubmit={(event) => {
              void handleSubmit(handleManagerTermsSubmit)(event);
            }}
          >
            <Box sx={guaranteesClientPageStyles.flowFieldsGrid}>
              <FormInput
                name={GuaranteesManagerTermsFieldName.DebtType}
                label={MANAGER_TERMS_TEXT.debtTypeLabel}
                inputProps={{ readOnly: true }}
                sx={guaranteesClientPageStyles.flowField}
              />
              {hasBeneficiarySanctionsMatch && (
                <FormAutocomplete
                  name={GuaranteesManagerTermsFieldName.BeneficiarySanctionsStatus}
                  label={MANAGER_TERMS_TEXT.beneficiarySanctionsStatusLabel}
                  options={[...BENEFICIARY_SANCTIONS_STATUS_OPTIONS]}
                  disabled={areFieldsDisabled}
                  sx={guaranteesClientPageStyles.flowField}
                />
              )}
            </Box>

            <Box sx={guaranteesClientPageStyles.flowActions}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSaveDisabled}
                startIcon={saveStartIcon}
                sx={dataFormStyles.documentPrimaryAction}
              >
                {MANAGER_TERMS_TEXT.saveButton}
              </Button>
              {showConfirmButton && (
                <Button
                  type="button"
                  variant="contained"
                  disabled={isConfirmDisabled}
                  onClick={handleConfirmClick}
                  startIcon={confirmStartIcon}
                  sx={dataFormStyles.documentPrimaryAction}
                >
                  {MANAGER_TERMS_TEXT.submitStep3Button}
                </Button>
              )}
            </Box>
          </Box>
        </FormProvider>
      )}
    </FlowSection>
  );
};

export default ManagerTermsSection;
